import { Component, ViewEncapsulation, OnInit, OnDestroy,
	ViewChildren, QueryList, ElementRef } from '@angular/core';
import { Subscription, debounceTime, merge } from 'rxjs';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import moment from 'moment';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor, TitleCasePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

import { WizardStepperService } from '../../../services/wizard-stepper.service';
import { WizardStep } from '../../../models/app-wizard-state';
import { AppWizardService } from '../../../services/app-wizard.service';
import { WizardPageContent } from '../wizard-page-content';
import { FormOffer, FormFamilyByVolumes, FormProducts } from '../../../models/form-offer';
import { OFFER_TYPE } from '../../../models/offer-utils';
import { isoMonthParse, isoMonthToDate,
	isoMonthToMoment } from '../../../models/iso-week-or-month-utils';
import { PeriodIsoMonth } from '../../../models/month-week-value';
import { OfferProduct, ProductFamily } from '../../../models/offer';
import { FamilyOfProducts } from '../../../models/offer-wizard';
import { UNDERSCORE } from '../../../../shared/utils/constant';
import { TranslationService } from '../../../../shared/services/translation.service';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { OfferPcpSplitComponent } from '../../utils/offer-pcp-split/offer-pcp-split.component';
import { ProductListQuantityComponent } from '../../utils/product-list-quantity/product-list-quantity.component';
import { IconService } from '../../../../shared/services/icon.service';

@Component({
	selector: 'copo-offer-wizard-step-quantity',
	templateUrl: './step-quantity.component.html',
	styleUrl: './step-quantity.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		LoadingSpinnerComponent,
		TranslateModule,
		MatTooltipModule,
		MatFormFieldModule,
		FormsModule,
		ReactiveFormsModule,
		NgFor,
		TitleCasePipe,
		OfferPcpSplitComponent,
		ProductListQuantityComponent,
		MatIconModule
	]
})
export class StepQuantityComponent extends WizardPageContent implements OnDestroy, OnInit {
	@ViewChildren('rows') rows!: QueryList<ElementRef<HTMLElement>>;
	@ViewChildren('table') readonly tables!: QueryList<ProductListQuantityComponent>;
	@ViewChildren('offerPcpSplit') readonly offerFamilyByVolume!: QueryList<OfferPcpSplitComponent>;

	readonly UNDERSCORE = UNDERSCORE;
	readonly form: FormOffer;

	rfqRef = '';
	soldTo = '';
	shipTo = '';
	endUser = '';
	period = '';
	productFamilies: ProductFamily[] = [];
	productsGroupedByFamily = new Map<string, FamilyOfProducts>();
	selectedPeriodDateDisplay = '';
	selectedPeriodDisplay = '';
	offerType: string | null | undefined;
	quantitySubscription?: Subscription;

	isLoading = true;
	isMerged = false;

	private readonly removeProductFamilySubscription: Subscription[] = [];
	private readonly VIEW_STEP_NUM = 2;
	private selectedPeriod: PeriodIsoMonth | undefined;

	constructor(
		private readonly appWizardService: AppWizardService,
		private readonly stepperService: WizardStepperService,
		readonly iconService: IconService,
		private readonly translationService: TranslationService
	) {
		iconService.registerAllIcons();
		super(WizardStep.QUANTITY, appWizardService.getState());
		const state = this.appWizardService.getState();
		this.offerType = state.offer.getOfferType() || sessionStorage.getItem(OFFER_TYPE);
		this.form = this.appWizardService.getStateOfferForm();
	}

	get getProductFamiliesHavingItemInCart() {
		return this.productFamilies.filter(
			productFamily => productFamily.code && this.getProductsCountByCode(productFamily.code) > 0
		);
	}

	get isError() {
		return this.appWizardService.getState().errorInPage;
	}

	ngOnInit() {
		this.stepperService.onStepChanged.emit({ step: this.VIEW_STEP_NUM });
		if (this.form.getFormPeriod().getFormPeriodStartMonth().value) {
			this.selectedPeriod = new PeriodIsoMonth(
				isoMonthParse(<string> this.form.getFormPeriod().getFormPeriodStartMonth().value),
				isoMonthParse(<string> this.form.getFormPeriod().getFormPeriodEndMonth().value)
			);
		}
		this.rfqRef = <string> this.form.getFormRfqRef().value;
		const selSoldToId = <string> this.form.getFormSoldTo().value;
		if (this.appWizardService.soldToList) {
			const selectedSoldTo = this.appWizardService.soldToList.find(v => v.businessId === selSoldToId);
			if (selectedSoldTo) {
				this.soldTo = `${ selectedSoldTo.name } / ${ selectedSoldTo.city }`;
			}
		}

		const selShipToId = <string> this.form.getFormShipTo().value;
		if (this.appWizardService.shipToList) {
			const selectedShipTo = this.appWizardService.shipToList.find(v => v.businessId === selShipToId);
			if (selectedShipTo) {
				this.shipTo = `${ selectedShipTo.name } / ${ selectedShipTo.city }`;
			}
		}

		const selEndUserId = <string> this.form.getFormEndUser().value;
		if (this.appWizardService.endUserList) {
			const selectedEndUser = this.appWizardService.endUserList.find(v => v.businessId === selEndUserId);
			if (selectedEndUser) {
				this.endUser = `${ selectedEndUser.name } / ${ selectedEndUser.city }`;
			}
		}
		this.removeDuplicateProducts(<OfferProduct[]> this.form.getFormProducts().value);
		this.createFormProductsByFamily(selSoldToId);
		if (this.offerType !== 'spot') {
			this.addFormProductsByFamily();
		}
		this.processPeriodInfo();
		this.generateQuantityForProductFamily();
		for (const formProduct of this.form.getFormProducts().getFormProducts()) {
			this.quantitySubscription = merge(
				formProduct.getFormQuantity().valueChanges
			).pipe(
				debounceTime(500)
			).subscribe(
				() => {
					this.generateQuantityForProductFamily();
				}
			);
		}
		this.isLoading = false;
	}

	ngOnDestroy() {
		for (const sub of this.removeProductFamilySubscription) {
			sub.unsubscribe();
		}
		if (this.quantitySubscription) {
			this.quantitySubscription.unsubscribe();
		}
	}

	checkError() {
		let isError = false;
		let errorFamilyCodeToScroll = '';
		if (this.offerType === 'spot') {
			for (const table of this.tables.toArray()) {
				const errorFamilyCode = table.checkError(!isError);
				if (errorFamilyCode !== '' && errorFamilyCodeToScroll === '') {
					errorFamilyCodeToScroll = errorFamilyCode;
					isError = true;
				}
			}
		} else {
			for (const offerPcpSplit of this.offerFamilyByVolume.toArray()) {
				const errorFamilyCode = offerPcpSplit.checkQuantity(!isError);
				if (errorFamilyCode !== '' && errorFamilyCodeToScroll === '') {
					errorFamilyCodeToScroll = errorFamilyCode;
					isError = true;
				}
			}
		}
		const indexToScroll = this.getProductFamiliesHavingItemInCart
			.findIndex(productFamily => productFamily.code === errorFamilyCodeToScroll);
		if (indexToScroll !== -1) {
			const rowItems = this.rows.toArray();
			if (rowItems[indexToScroll]) {
				rowItems[indexToScroll].nativeElement.scrollIntoView();
			}
		}
		return isError;
	}

	checkErrorForDraft() {
		return false;
	}

	scrollToGivenFamily(index: number) {
		const rowItems = this.rows.toArray();
		if (rowItems[index]) {
			rowItems[index].nativeElement.scrollIntoView();
		}
	}

	getFormFamilyByVolumes(code: string) {
		return this.productsGroupedByFamily.get(code)?.formFamilyByVolumes;
	}

	getQuantityByFamilyCode(code: string) {
		return this.productsGroupedByFamily.get(code)?.totalQuantity || 0;
	}

	quantityCalculationEvent(data: { familyCode: string; totalQuantity: number }) {
		const familyOfProduct = this.productsGroupedByFamily.get(data.familyCode);
		if (familyOfProduct) {
			familyOfProduct.totalQuantity = data.totalQuantity;
		}
	}

	getFromDate() {
		if (!this.selectedPeriod) {
			throw new Error('undefined selected period');
		}
		return isoMonthToDate(this.selectedPeriod.start);
	}

	getToDate() {
		if (!this.selectedPeriod) {
			throw new Error('undefined selected period');
		}
		return isoMonthToMoment(this.selectedPeriod.end).endOf('month').toDate();
	}

	getProductsCountByCode(code: string) {
		return this.appWizardService.productsGroupedByFamily.get(code) ?? 0;
	}

	getUniqueRefListByCode(code: string) {
		const products = this.productsGroupedByFamily.get(code)?.formProducts.getProducts();
		if (products && products.length > 0) {
			return products.map(product => product.uniqueRef)
				.filter(uniqueRef => uniqueRef !== undefined);
		}
		return [];
	}

	getformProductsByCode(code: string) {
		return this.productsGroupedByFamily.get(code)?.formProducts || new FormProducts();
	}

	removeDuplicateProducts(products: OfferProduct[]): OfferProduct[] {
		const seen = new Set<string>();

		return products.filter(product => {
			const keyParts = [
				product.inquiryNumber ?? '',
				product.steelGrade,
				product.thicknessValue,
				product.widthValue,
				product.lengthValue,
				product.variableFormGroup
			];

			const key = keyParts.map(val =>
				typeof val === 'object' ? JSON.stringify(val) : String(val)).join('|');
			if (seen.has(key) && product.uniqueRef) {
				this.isMerged = true;
				this.form.getFormProducts().removeProductByUniqueRef(product.uniqueRef);
				const familyCode = `${ product.category }#${ product.subCategory }#${ product.form }`;
				const productCount = this.appWizardService.productsGroupedByFamily.get(familyCode);
				if (productCount) {
					this.appWizardService.productsGroupedByFamily.set(familyCode, productCount - 1);
				}
				this.appWizardService.getState().ui.removeSimulationData(product.uniqueRef);
				return false;
			}
			seen.add(key);
			return true;
		});
	}

	getProductsCountBySubCategory(key: string) {
		if (this.productsGroupedByFamily.has(key)) {
			return this.productsGroupedByFamily.get(key) ?? 0;
		}
		return 0;
	}

	private processPeriodInfo() {
		let startMonthValue = <string> this.form.getFormPeriod().getFormPeriodStartMonth().value;
		let endMonthValue = <string> this.form.getFormPeriod().getFormPeriodEndMonth().value;

		const currentDate = new Date();
		if (startMonthValue) {
			const [startYear, startMonth] = startMonthValue.split('-M').map(Number);
			const startDate = new Date(startYear, startMonth - 1, 1);
			if (startDate < currentDate) {
				this.form.getFormPeriod().getFormPeriodStartMonth().setValue('');
				startMonthValue = '';
			}
		}

		if (endMonthValue) {
			const [endYear, endMonth] = endMonthValue.split('-M').map(Number);
			const endDate = new Date(endYear, endMonth - 1, 1);
			if (endDate < currentDate) {
				this.form.getFormPeriod().getFormPeriodEndMonth().setValue('');
				endMonthValue = '';
			}
		}

		if (!this.form.getFormPeriod().getFormPeriodStartMonth().value
			|| !this.form.getFormPeriod().getFormPeriodEndMonth().value) {
			this.selectedPeriodDateDisplay = this.translationService.getTR('global.none');
		}

		if (startMonthValue && endMonthValue) {
			const [startYear, startMonth] = startMonthValue.split('-M').map(Number);
			const [endYear, endMonth] = endMonthValue.split('-M').map(Number);
			const monthDifference = (endYear - startYear) * 12 + (endMonth - startMonth);

			const selectedPeriodType =
					startYear === endYear && startMonth === endMonth ? 'Monthly'
						: monthDifference === 2 && [1, 4, 7, 10].includes(startMonth) ? 'Quarterly'
							: monthDifference === 5 && [1, 7].includes(startMonth) ? 'Semesterly'
								: startMonth === 1 && endMonth === 12 && startYear === endYear ? 'Annually'
									: 'Custom Period';
			switch (selectedPeriodType) {
			case 'Monthly':
				this.selectedPeriodDisplay = `${ moment().month(startMonth - 1).format('MMMM') } (${ startYear })`;
				break;

			case 'Quarterly':
				this.selectedPeriodDisplay = `${ this.translationService.getTR('eoffer-Quarter') }
				 ${ Math.floor((startMonth - 1) / 3) + 1 } (${ startYear })`;
				break;

			case 'Semesterly':
				this.selectedPeriodDisplay = `${ this.translationService.getTR('eoffer-Semester') }
				 ${ Math.floor((startMonth - 1) / 6) + 1 } (${ startYear })`;
				break;

			case 'Annually':
				this.selectedPeriodDisplay = `${ startYear }`;
				break;

			default:
				this.selectedPeriodDisplay = '';
				break;
			}
		}
		this.updateSelectedPeriodDisplay(startMonthValue, endMonthValue);
	}

	private updateSelectedPeriodDisplay(fromMonth: string, toMonth?: string) {
		if (fromMonth && toMonth) {
			this.selectedPeriodDateDisplay = `${ fromMonth } - ${ toMonth }`;
		} else if (fromMonth) {
			this.selectedPeriodDateDisplay = `${ fromMonth } -`;
		} else if (toMonth) {
			this.selectedPeriodDateDisplay = `- ${ toMonth }`;
		} else {
			this.selectedPeriodDateDisplay = this.translationService.getTR('global.none');
		}
	}

	private createFormProductsByFamily(soldTo: string) {
		this.productFamilies = this.appWizardService.getState().ui.getProductFamilies(soldTo) || [];
		const formProucts = this.form.getFormProducts();
		for (const formProduct of formProucts.getFormProducts()) {
			const product = formProduct.getProduct();
			const productFamily = this.productFamilies.find(
				pf =>
					product.category === pf.category
					&& product.subCategory === pf.subCategory
					&& product.form === pf.form
			);
			if (productFamily) {
				let familyOfProduct = this.productsGroupedByFamily.get(productFamily.code);
				if (familyOfProduct) {
					familyOfProduct.formProducts.push(formProduct);
					continue;
				}
				const localFormProducts = new FormProducts(formProucts);
				localFormProducts.push(formProduct);
				familyOfProduct = {
					familyCode: productFamily.code,
					formProducts: localFormProducts
				};
				this.productsGroupedByFamily.set(productFamily.code, familyOfProduct);
				this.removeProductFamilySubscription.push(localFormProducts.valueChanges.subscribe({
					next: () => {
						this.generateQuantityForProductFamily();
						if (localFormProducts.length !== 0) {
							return;
						}
						if (this.productsGroupedByFamily.has(familyOfProduct.familyCode)) {
							this.productsGroupedByFamily.delete(familyOfProduct.familyCode);
						}
					},
					error: (err: unknown) => {
						console.error(err);
					}
				}));
			}
		}
	}

	private addFormProductsByFamily() {
		const globalFormFamilyByVolumes = this.appWizardService.getStateOfferForm().getFormFamilyByVolumes();
		for (const [, familyofProduct] of this.productsGroupedByFamily) {
			const localFamilyByVolumes = new FormFamilyByVolumes(globalFormFamilyByVolumes);
			for (const formFamilyByVolume of globalFormFamilyByVolumes.getFormFamilyByVolumes()) {
				if (formFamilyByVolume.getFamilyByVolume().familyCode === familyofProduct.familyCode) {
					localFamilyByVolumes.push(formFamilyByVolume);
				}
			}
			familyofProduct.formFamilyByVolumes = localFamilyByVolumes;
		}
	}

	private generateQuantityForProductFamily() {
		for (const [,familyOfProduct] of this.productsGroupedByFamily) {
			familyOfProduct.totalQuantity = 0;
			if (this.offerType === 'spot') {
				for (const product of familyOfProduct.formProducts.getFormProducts()) {
					const quantity = <number | undefined> product.getFormQuantity().value;
					if (quantity) {
						familyOfProduct.totalQuantity += Number(quantity);
					}
				}
			} else {
				const globalFormFamilyByVolumes = this.appWizardService.getStateOfferForm().getFormFamilyByVolumes();
				for (const formFamilyByVolume of globalFormFamilyByVolumes.getFormFamilyByVolumes()) {
					if (formFamilyByVolume.getFamilyByVolume().familyCode === familyOfProduct.familyCode) {
						const quantity = <number | undefined> formFamilyByVolume.getFormQuantity().value;
						if (quantity) {
							familyOfProduct.totalQuantity += Number(quantity);
						}
					}
				}
			}
		}
	}
}
